import React, { useState } from 'react';
import useToastWithSound from '../../hooks/useToastWithSound';
import api from '../../ui/api';
import useData from '../useData';
import useDirtyForm from '../useDirtyForm';
import { useNavIntent } from '../navIntent';
import PageShell, { Button } from '../../ui/PageShell';
import Table from '../../ui/Table';
import Modal from '../../ui/Modal';
import { FormGrid, Field, Input, Select } from '../../ui/Form';
import PhoneInput from '../../ui/PhoneInput';
import { formatPhone } from '../../ui/phone';
import HelpButton from '../help/HelpButton';
import { useAuth } from '../auth';
import EmptyGuide from '../help/EmptyGuide';

export default function Transportation() {
  const toast = useToastWithSound();
  const { isAdmin } = useAuth();
  const { data, loading, error, reload } = useData(() => api.transportation(), []);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ driver_name: '', driver_phone: '', bus_number: '', id_number: '', vehicle_details: '', monthly_fee: 0, status: 'active' });
  const [saving, setSaving] = useState(false);
  const dirty = useDirtyForm(form);

  const openNew = () => {
    const next = { driver_name: '', driver_phone: '', bus_number: '', id_number: '', vehicle_details: '', monthly_fee: 0, status: 'active' };
    setForm(next); dirty.reset(next); setEditing({});
  };
  const openEdit = (t) => { setForm({ ...t }); dirty.reset({ ...t }); setEditing(t); };

  // Navbar search → «بيانات السائق»: open this bus's form.
  useNavIntent('transportation', (it) => {
    if (!data) return false;
    const t = data.find((r) => r.id === it.driverId);
    if (t) openEdit(t);
  }, [data]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.driver_name.trim() || !form.bus_number.trim()) return toast.error('اسم السائق ورقم السيارة مطلوبان');
    setSaving(true);
    try {
      if (editing.id) { await api.updateTransportation(editing.id, form); toast.success('تم التحديث'); }
      else { await api.createTransportation(form); toast.success('تمت الإضافة'); }
      setEditing(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <PageShell title="المواصلات" description="سيارات النقل المدرسي والسائقون"
      actions={(
        <>
          <HelpButton guides={['bus-add']} />
          <Button onClick={openNew} data-guide="buses-add">+ سيارة جديدة</Button>
        </>
      )}>
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Table
        keyField="id"
        rows={data || []}
        loading={loading}
        statusKey="status"
        emptyText={<EmptyGuide text="لا توجد سيارات مسجّلة" guide="bus-add" />}
        searchPlaceholder="ابحث بالسائق أو رقم السيارة..."
        onEdit={openEdit}
        columns={[
          { key: 'driver_name', label: 'السائق', width: 2, filterable: false, render: (r) => <strong>{r.driver_name}</strong> },
          { key: 'bus_number', label: 'رقم السيارة', nowrap: true },
          { key: 'driver_phone', label: 'هاتف السائق', nowrap: true, filterable: false,
            render: (r) => (r.driver_phone ? <bdi dir="ltr">{formatPhone(r.driver_phone)}</bdi> : '—') },
          { key: 'vehicle_details', label: 'تفاصيل المركبة' },
          ...(isAdmin ? [{ key: 'monthly_fee', label: 'الأجرة الشهرية', nowrap: true, align: 'end', filterable: false,
            formatValue: (v) => `$${(v || 0).toLocaleString('en-US')}` }] : []),
          { key: 'status', label: 'الحالة', nowrap: true, align: 'center',
            badge: (v) => (v === 'active' ? 'success' : 'neutral'),
            formatValue: (v) => (v === 'active' ? 'نشط' : 'متوقف') },
        ]}
      />

      {editing && (
        <Modal open size="md" title={editing.id ? `تعديل: ${editing.driver_name}` : 'سيارة جديدة'}
          onClose={() => setEditing(null)}
          confirmClose={dirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button variant="success" onClick={save} loading={saving} data-guide="modal-save">حفظ</Button>
            </>
          )}>
          <form onSubmit={save}>
            <FormGrid columns={2}>
              <Field label="اسم السائق" required guide="bus-driver"><Input value={form.driver_name} onChange={set('driver_name')} /></Field>
              <Field label="هاتف السائق"><PhoneInput value={form.driver_phone || ''} onChange={(v) => setForm((f) => ({ ...f, driver_phone: v }))} /></Field>
              <Field label="رقم السيارة" required guide="bus-number"><Input value={form.bus_number} onChange={set('bus_number')} /></Field>
              <Field label="رقم الهوية"><Input value={form.id_number || ''} onChange={set('id_number')} /></Field>
              {isAdmin && <Field label="الأجرة الشهرية" guide="bus-fee"><Input type="number" value={form.monthly_fee} onChange={set('monthly_fee')} /></Field>}
              <Field label="تفاصيل المركبة" span><Input value={form.vehicle_details || ''} onChange={set('vehicle_details')} /></Field>
              <Field label="الحالة" span>
                <Select value={form.status} onChange={set('status')}
                  options={[{ value: 'active', label: 'نشط' }, { value: 'inactive', label: 'متوقف' }]} />
              </Field>
            </FormGrid>
          </form>
        </Modal>
      )}
    </PageShell>
  );
}
